/** 向导第 ③ 步的「测试附加费用」沙盒 + 修饰项说明（移植自旧版 admin.html 修饰项弹窗） */
import { useState } from "react";
import { FlaskConical, BookOpen, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, Button, Field, Input } from "@/components/ui";
import type { Modifier } from "../api";
import { runModifierSandbox, type SandboxResult } from "./modifierSandbox";

export function ModifierSandbox({ modifiers }: { modifiers: Modifier[] }) {
  const [qty, setQty] = useState("5");
  const [edge, setEdge] = useState("");
  const [base, setBase] = useState("10");
  const [doubleSided, setDoubleSided] = useState(false);
  const [cutting, setCutting] = useState(false);
  const [smallBatch, setSmallBatch] = useState(false);
  const [result, setResult] = useState<SandboxResult | null>(null);

  const run = () => setResult(runModifierSandbox(modifiers, {
    qty: parseInt(qty) || 1,
    edgeLen: parseFloat(edge) || 0,
    baseUnitPrice: parseFloat(base) || 0,
    doubleSided, cutting, smallBatch,
  }));

  const check = (checked: boolean, onChange: (v: boolean) => void, label: string) => (
    <label className="flex items-center gap-1.5">
      <input type="checkbox" className="h-4 w-4 accent-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex items-center gap-1.5 border-b bg-primary-soft px-3 py-2 text-xs font-medium text-primary">
        <FlaskConical className="h-3.5 w-3.5" /> 测试当前附加费用配置
      </div>
      <div className="space-y-3 p-3 text-sm">
        <p className="text-xs text-muted-foreground">填入订单参数，验证附加费用是否按预期生效（前端估算，最终以实际报价为准）：</p>
        <div className="grid items-end gap-2 sm:grid-cols-3">
          <Field label={<span className="text-xs">数量（张/个）</span>}>
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="tabular-nums" />
          </Field>
          <Field label={<span className="text-xs">包边长度（米，选填）</span>}>
            <Input type="number" min={0} step={0.1} value={edge} placeholder="无包边"
              onChange={(e) => setEdge(e.target.value)} className="tabular-nums" />
          </Field>
          <Field label={<span className="text-xs">基础单价（元/张或㎡）</span>}>
            <Input type="number" min={0} step={0.1} value={base} onChange={(e) => setBase(e.target.value)} className="tabular-nums" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {check(doubleSided, setDoubleSided, "双面")}
          {check(cutting, setCutting, "需要裁切")}
          {check(smallBatch, setSmallBatch, "小批量场景")}
        </div>
        <Button size="sm" icon={<FlaskConical />} onClick={run}>运行测试</Button>
        {result && (
          <div className="space-y-2">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs leading-6">
              {result.lines.map((l, i) => (
                <div key={i}>
                  {l.text} {l.value && <strong className="tabular-nums">{l.value}</strong>}
                  {l.status === "on" && (
                    <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-success-strong">
                      <CheckCircle2 className="h-3 w-3" /> 生效
                    </span>
                  )}
                  {l.status === "off" && <span className="ml-1 text-muted-foreground">不生效</span>}
                </div>
              ))}
              <div className="mt-1 border-t pt-1">
                合计：<strong className="text-sm tabular-nums text-price">{result.total.toFixed(2)} 元</strong>，
                单价 <span className="tabular-nums">{result.unitPrice.toFixed(2)}</span> 元/张
              </div>
            </div>
            {result.warnings.length > 0 && (
              <Alert variant="warning" className="text-xs">
                {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </Alert>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const HELP: Array<{ name: string; desc: string[] }> = [
  { name: "包边费（元/米）", desc: ["公式：包边长度(米) × 每米价格 × 数量", "触发：客户报了包边长度时"] },
  { name: "裁切费（元/张）", desc: ["公式：每张裁切费 × 数量", "触发：客户要求裁切时（未设置时默认 0.5 元/张）"] },
  { name: "双面倍数", desc: ["公式：基础价 × 倍数，如 1.6", "触发：客户要求双面时（未设置时默认 1.6）"] },
  { name: "最低起价（元）", desc: ["触发：整单低于起价时按起价收"] },
  { name: "小批量特价", desc: ["前 N 张按档位价，其余按设置的每张单价", "仅当特价比正常价更低时生效", "仅适用于按面积、按张（固定价）计价的档位"] },
];

/** 修饰项说明（可折叠） */
export function ModifierHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border bg-card text-xs">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-left font-medium text-foreground transition-colors hover:bg-muted",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <BookOpen className="h-3.5 w-3.5 text-muted-foreground" /> 附加费用说明
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 leading-5">
          <p className="text-muted-foreground">在基础报价之上叠加的额外费用或价格调整规则，每项独立计算后累加。</p>
          <div className="divide-y rounded-md border bg-card">
            {HELP.map((h) => (
              <div key={h.name} className="px-3 py-2">
                <div className="font-medium text-primary">{h.name}</div>
                {h.desc.map((d) => <div key={d} className="text-muted-foreground">{d}</div>)}
              </div>
            ))}
          </div>
          <Alert variant="warning" className="text-xs">
            以上 5 种之外的自定义附加规则会被保存，但不参与报价计算；向导编辑时会原样保留它们。
          </Alert>
        </div>
      )}
    </div>
  );
}
